import { useNavigate } from 'react-router-dom'

const APP_VERSION = '0.1.0'

const TOOLS = [
  {
    label: 'Point',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    description: (
      <p className="text-sm text-muted-foreground">
        Single click on the map to drop a point. Captures a single coordinate. Use for marking a
        specific location, target, or observation post.
      </p>
    ),
  },
  {
    label: 'Polygon',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 2 19h20L12 2z" />
      </svg>
    ),
    description: (
      <p className="text-sm text-muted-foreground">
        Click to place each vertex of a free-form area.{' '}
        <strong>Double-click</strong> the last point (or click the first point) to close and
        complete the shape. Minimum 3 vertices required. Use for irregular areas of interest,
        zones, or boundaries.
      </p>
    ),
  },
  {
    label: 'Line',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
    description: (
      <p className="text-sm text-muted-foreground">
        Click to place each vertex along a route or axis.{' '}
        <strong>Double-click</strong> to finish. Use for routes, lines of bearing, phase lines,
        or axis of advance.
      </p>
    ),
  },
  {
    label: 'BBox',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" />
      </svg>
    ),
    description: (
      <p className="text-sm text-muted-foreground">
        Two-click axis-aligned bounding box.{' '}
        <strong>Click 1</strong> sets one corner, <strong>Click 2</strong> sets the opposite
        corner. Always produces a north-up rectangle. Use for grid squares, target boxes, or
        search areas aligned to the cardinal axes.
      </p>
    ),
  },
  {
    label: 'Ellipse',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="12" rx="10" ry="6" />
      </svg>
    ),
    description: (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          3-click workflow matching ArcGIS and military GIS convention:
        </p>
        <ol className="space-y-1.5 list-none pl-0">
          <li className="flex gap-2 items-start">
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground flex-shrink-0 mt-0.5">1</span>
            <span className="text-sm text-muted-foreground">
              <strong>Center</strong> &mdash; click to set the center of the ellipse.
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground flex-shrink-0 mt-0.5">2</span>
            <span className="text-sm text-muted-foreground">
              <strong>Major axis end</strong> &mdash; click to set the semi-major radius and
              azimuth. A circle preview tracks your cursor so you can gauge the size.
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-foreground flex-shrink-0 mt-0.5">3</span>
            <span className="text-sm text-muted-foreground">
              <strong>Minor axis end</strong> &mdash; click to set the semi-minor axis. The
              perpendicular distance from your click to the major axis determines the width.
              The ellipse finalises on this click.
            </span>
          </li>
        </ol>
        <p className="text-sm text-muted-foreground">
          Use for CEP rings, threat envelopes, error ellipses, or any rotated oval area. Press{' '}
          <kbd className="font-mono text-xs bg-muted px-1 py-0.5 rounded border border-border">
            Esc
          </kbd>{' '}
          at any point to cancel.
        </p>
      </div>
    ),
  },
]

export default function AboutPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">About</h1>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; Back to Map
          </button>
        </div>

        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            How To Use
          </h2>
          <ol className="list-decimal list-inside text-sm space-y-1">
            <li>Open Settings and configure a WMS source (endpoint + layers), then save.</li>
            <li>From the map, pick a drawing tool (Point, Line, Polygon, BBox, or Ellipse).</li>
            <li>Complete the collection workflow: select template, fill fields, review, submit.</li>
            <li>Use &ldquo;Save Home View&rdquo; to persist your preferred center/zoom for next login.</li>
            <li>Optionally switch WMS sources from the map source selector.</li>
          </ol>
        </section>

        <section className="rounded-lg border border-border bg-card p-4 space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Drawing Tools
          </h2>
          <div className="space-y-5">
            {TOOLS.map(({ label, icon, description }) => (
              <div key={label} className="flex gap-3">
                <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-md bg-muted flex items-center justify-center text-foreground">
                  {icon}
                </div>
                <div className="space-y-1 min-w-0">
                  <p className="text-sm font-medium">{label}</p>
                  {description}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Version
          </h2>
          <p className="text-sm font-mono">v{APP_VERSION} &mdash; Alpha</p>
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            This software is in alpha. Features may be incomplete, change without notice, or
            behave unexpectedly. Not intended for operational use.
          </p>
        </section>

        <section className="rounded-lg border border-border bg-card p-4 space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Author
          </h2>
          <p className="text-sm">Pedro FernandezRamirez</p>
        </section>
      </div>
    </div>
  )
}
